/**
 * Image validation utilities for product uploads
 * Provides validation functions for file format, size, and overall image validation
 */

import toast from 'react-hot-toast';
import { 
  ALLOWED_FORMATS, 
  ALLOWED_MIME_TYPES, 
  MAX_FILE_SIZE,
  ERROR_MESSAGES,
  formatFileSize
} from '../constants/imageUpload';

/**
 * Validates if the file format is supported
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validateFileFormat = (file) => {
  if (!file) {
    return { isValid: false, error: ERROR_MESSAGES.NO_FILE };
  }

  // Get file extension from filename
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.split('.').pop();

  // Get MIME type from file
  const fileMimeType = file.type.toLowerCase();

  // Check file extension and MIME type using constants
  const isValidExtension = ALLOWED_FORMATS.includes(fileExtension);
  const isValidMimeType = ALLOWED_MIME_TYPES.includes(fileMimeType);

  // Both extension and MIME type must be valid for security
  if (!isValidExtension || !isValidMimeType) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.FORMAT
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validates if the file size is within limits
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validateFileSize = (file) => {
  if (!file) {
    return { isValid: false, error: ERROR_MESSAGES.NO_FILE };
  }

  if (file.size > MAX_FILE_SIZE) {
    const fileSizeFormatted = formatFileSize(file.size);
    return {
      isValid: false,
      error: ERROR_MESSAGES.SIZE_WITH_CURRENT(fileSizeFormatted.replace('MB', ''))
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validates an image file for both format and size
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { isValid: false, errors: [ERROR_MESSAGES.NO_FILE] };
  }

  const errors = [];
  
  // Validate file format
  const formatValidation = validateFileFormat(file);
  if (!formatValidation.isValid) {
    errors.push(formatValidation.error);
  }
  
  // Validate file size
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.isValid) {
    errors.push(sizeValidation.error);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Generates user-friendly error messages for validation failures
 * @param {string} errorType - The type of validation error
 * @param {Object} context - Additional context for the error message
 * @returns {string} - User-friendly error message
 */
export const generateErrorMessage = (errorType, context = {}) => {
  switch (errorType) {
    case 'FORMAT':
      return ERROR_MESSAGES.FORMAT;
    case 'SIZE':
      return context.fileSizeInMB 
        ? ERROR_MESSAGES.SIZE_WITH_CURRENT(context.fileSizeInMB)
        : ERROR_MESSAGES.SIZE;
    case 'NO_FILE':
      return ERROR_MESSAGES.NO_FILE;
    case 'MULTIPLE_ERRORS':
      return ERROR_MESSAGES.MULTIPLE_ERRORS;
    default:
      return ERROR_MESSAGES.INVALID_FILE;
  }
};

/**
 * Shows validation error using toast notification
 * @param {string|string[]} errors - Error message(s) to display
 */
export const showValidationError = (errors) => {
  if (Array.isArray(errors)) {
    errors.forEach(error => toast.error(error));
  } else {
    toast.error(errors);
  }
};

/**
 * Validates image file and shows toast notification on error
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateImageFileWithToast = (file) => {
  const validation = validateImageFile(file);
  
  if (!validation.isValid) {
    showValidationError(validation.errors);
  }
  
  return validation;
};
